import { useState } from "react"

import { Flex, Form, message, Typography, Upload } from "antd"
import cls from "classnames"

import { AttachmentsList } from "../../Containers/CreditsPage/components/Attachments"
import Icon from "../../Icon"
import ButtonComponent from "../Button"
import TextAreaComponent from "../Input/TextArea"

import styles from "./AddCommentRequestForm.module.css"

const { Text, Link } = Typography

const MAX_TOTAL_SIZE_MB = 10

function AddCommentRequestForm({ onClose, onSubmit, initialValues, isEdit }) {
  const [form] = Form.useForm()
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [deletedFiles, setDeletedFiles] = useState([])
  const availableCountFilesTotal =
    20 - (initialValues?.["admin_screenshots"]?.length || 0) + deletedFiles?.length
  const availableCountFiles = availableCountFilesTotal - uploadedFiles?.length

  const commentValue = Form.useWatch("admin_comment", form)
  const fileListValue = Form.useWatch("file_list", form) || []

  const remainingInitialScreenshots =
    initialValues?.admin_screenshots?.filter((file) => !deletedFiles?.includes(file?.id)) || []
  const hasComment = !!commentValue?.trim()
  const hasUploadedFiles = !!fileListValue?.length
  const hasExistingFiles = !!remainingInitialScreenshots?.length
  const commentChanged = commentValue !== (initialValues?.admin_comment ?? "")
  const hasChanges = deletedFiles.length > 0 || commentChanged || hasUploadedFiles
  const isSubmitDisabled = isEdit
    ? !hasChanges
    : !(hasComment || hasUploadedFiles || hasExistingFiles)

  const beforeUpload = (file) => {
    const allowedExtensions = ["png", "jpeg", "jpg"]
    const fileExtension = file.name.split(".").pop().toLowerCase()
    if (!allowedExtensions.includes(fileExtension)) {
      message.error(`${file.name} has an unsupported format.`)
      return Upload.LIST_IGNORE
    }

    // Calculate the total weight: previously uploaded files + new file
    const currentTotalSize = [...uploadedFiles, file].reduce(
      (acc, currFile) => acc + (currFile.size || 0),
      0
    )
    if (currentTotalSize / 1024 / 1024 > MAX_TOTAL_SIZE_MB) {
      message.error(`Total file size should not exceed ${MAX_TOTAL_SIZE_MB} MB.`)
      return Upload.LIST_IGNORE
    }
    return false
  }

  const handleUploadChange = (info) => {
    setUploadedFiles(info.fileList)
  }

  const onFinish = (values) => {
    const { file_list, admin_comment = null } = values
    onSubmit({
      file_list,
      admin_comment,
      ...(deletedFiles?.length ? { delete_files: deletedFiles } : {})
    })
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e, fieldName) {
    const field = fieldName || e.target.name || e.target.id
    handleClearError(e, field)
  }

  const addErrorIcon = (error) => (
    <span>
      <Icon type={"alert_triangle_error"} /> {error}
    </span>
  )

  const validateMessages = {
    required: addErrorIcon("Required fields were not filled.")
  }

  const normFile = (e) => {
    if (Array.isArray(e)) return e
    return e?.fileList
  }

  const handleDeletedFile = (file) => {
    setDeletedFiles((prev) => [...prev, file?.id])
  }

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      validateMessages={validateMessages}
      layout="vertical"
      form={form}
      {...(initialValues ? { initialValues } : {})}
    >
      <div style={{ maxHeight: "80vh", overflow: "auto" }}>
        <Form.Item name="admin_comment" label={"Comment"}>
          <TextAreaComponent
            rows={3}
            maxLength={500}
            name="admin_comment"
            placeholder={"Enter any additional or related information to this credit request..."}
            autoComplete="dontshow"
            onFocus={onFocusHandler}
          />
        </Form.Item>

        <Flex vertical gap={4} style={{ marginBottom: 8 }}>
          <Text>Attach screenshots</Text>
          <Text type={"secondary"} style={{ fontWeight: 300 }}>
            Possible applicable file formats: .png, .jpeg, .jpg.
          </Text>
        </Flex>

        <Form.Item
          label={null}
          name="file_list"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            {
              validator: (_, value) => {
                if (value && value.length > availableCountFilesTotal) {
                  return Promise.reject(new Error("You can upload a maximum of 20 files."))
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <Upload
            multiple
            maxCount={availableCountFilesTotal}
            accept={".png,.jpeg,.jpg"}
            beforeUpload={beforeUpload}
            onChange={handleUploadChange}
            listType="text"
            style={{ width: "100%" }}
            iconRender={(file) => (
              <Icon
                type={file?.type?.startsWith("image/") ? "pictures" : "document"}
                style={{ fontSize: 20, lineHeight: 1 }}
              />
            )}
            showUploadList={{
              extra: ({ size = 0 }) => (
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  ({(size / 1000).toFixed(0)}KB)
                </Text>
              ),
              showRemoveIcon: true,
              removeIcon: <Icon type={"delete"} style={{ fontSize: 20, lineHeight: 1 }} />
            }}
          >
            <ButtonComponent
              size="large"
              type="dashed"
              icon={<Icon type="arrow_out_of_box" />}
              className={cls("table-action-btn flex center", {
                [styles.uploadButtonDisabled]: availableCountFiles <= 0
              })}
              block
              disabled={availableCountFiles <= 0}
            >
              <Text type="secondary">Click to upload files</Text>
            </ButtonComponent>
          </Upload>
        </Form.Item>

        <AttachmentsList
          attachments={remainingInitialScreenshots}
          preview={true}
          onDelete={handleDeletedFile}
        />
      </div>
      <Flex gap={16} justify="end" style={{ padding: "32px 3px 3px 3px" }}>
        <ButtonComponent size="large" htmlType="button" onClick={onClose}>
          Cancel
        </ButtonComponent>

        <ButtonComponent size="large" type="primary" htmlType="submit" disabled={isSubmitDisabled}>
          {isEdit ? "Confirm changes" : "Submit"}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default AddCommentRequestForm
